import { getBlogNavInitialData } from '@/api/blog/blogNav';
import { getArticleType } from '@/api/posts/articleType';
import { getGeneralResults } from '@/api/posts/generalResults';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { BlogLayout } from '@/components/layout/withBlogLayout';
import { GeneralResults } from '@gik/blog/components/GeneralResults/GeneralResults';
import Taxonomy from '@gik/blog/Taxonomy';
import type { StaticProps } from '@gik/core/api/BaseAPIConfig';
import { baseFallback } from '@gik/core/api/BaseAPIConfig';
import { useNavigatableInitialData } from '@gik/core/hooks/hooks/useNavigatableInitialData';
import { NotFoundPage } from '@gik/core/pages/NotFound/NotFoundPage';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import { SWRConfig } from 'swr';

export default function GeneralResultsPage(props): React.ReactElement {
  const router = useRouter();
  const fallback = useNavigatableInitialData(props);
  if (router.isFallback) {
    // TODO: wrap each page with withFallback function
    return <div>Loading...</div>;
  }

  // TODO: move logic to getStaticProps (throw error, see product page for example)
  if (
    router.query.slug &&
    router.query.taxonomySlug &&
    fallback?.[fallback?.generalResultsKey] &&
    !fallback?.[fallback?.generalResultsKey].length
  ) {
    return <NotFoundPage />;
  }

  const taxonomySlug = router.query.taxonomySlug as string;
  const pageSlug = router.query.slug as string;

  return (
    <SWRConfig value={{ fallback: props.fallback }}>
      <GeneralResults pageSlug={pageSlug} taxonomySlug={taxonomySlug} />
    </SWRConfig>
  );
}

GeneralResultsPage.getLayout = function getLayout(page: React.ReactElement, layoutProps: PageLayoutProps) {
  return <BlogLayout {...layoutProps}>{page}</BlogLayout>;
};

async function _getStaticProps(context: GetServerSidePropsContext): Promise<StaticProps> {
  const pageSlug = context.params.slug as string;
  const taxonomySlug = context.params.taxonomySlug as string;

  const generalResultsResponse = getGeneralResults({ taxonomySlug, pageSlug });
  const { situationsResponse, tagsResponse } = getBlogNavInitialData();

  const articleTypeResponse = taxonomySlug == Taxonomy.ArticleType ? getArticleType({ slug: pageSlug }) : null;

  const [generalResults, situations, tags] = await Promise.all([
    generalResultsResponse.json,
    situationsResponse.json,
    tagsResponse.json,
  ]);

  return baseFallback(
    {
      [generalResultsResponse.key]: generalResults,
      [situationsResponse.key]: situations,
      [tagsResponse.key]: tags,
      ...(taxonomySlug == Taxonomy.ArticleType
        ? {
            [articleTypeResponse.key]: await articleTypeResponse.json,
          }
        : {}),
    },
    {
      generalResultsKey: generalResultsResponse.key,
    }
  );
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps));

export async function getStaticPaths() {
  if (process.env.RENDER_STATIC_PAGES_AT_BUILD_TIME === 'true') {
    const { situationsResponse, tagsResponse } = getBlogNavInitialData();

    const promises = [situationsResponse.json, tagsResponse.json];
    // @ts-ignore
    const [situations, tags] = await Promise.all(promises as unknown as never); // TODO fix typing

    const articleTypesSlugs = ['spotlight', 'being-supportive', 'recipe'];
    const situationSlugs = (situations as { slug: string }[]).map(({ slug }) => slug);
    const tagsSlugs = (tags as { slug: string }[]).map(({ slug }) => slug);

    const paths = [
      ...articleTypesSlugs.map(slug => ({
        params: {
          taxonomySlug: 'article-type',
          slug,
        },
      })),
      ...situationSlugs.map(slug => ({
        params: {
          taxonomySlug: 'situation',
          slug,
        },
      })),
      ...tagsSlugs.map(slug => ({
        params: {
          taxonomySlug: 'content_tag',
          slug,
        },
      })),
    ];
    return { paths, fallback: true };
  } else {
    return { paths: [], fallback: 'blocking' };
  }
}
